import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  Signal,
  untracked,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy,
  HostListener
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  ButtonStyleHelper,
  ConfigService,
  DEFAULT_CONFIG,
  EnvironmentService,
  IChainStoreListItem,
  IOpeningTimeInfo,
  IUiButtonSettings,
  MyChainstoreService,
  OpeningTimesService,
  StyleService,
  SystemService
} from 'libs/shared/src';
import { GsIconComponent } from 'libs/shared/src/components/gs-icon/gs-icon.component';
import { GoogleMapsRendererComponent } from '../google-maps-renderer/google-maps-renderer.component';
import { OpeningHoursComponent } from '../shared/opening-hours/opening-hours.component';

/** Delay before a typed search term is applied, mirroring the other searches in the project */
const SEARCH_DEBOUNCE_MS = 400;
/** Minimum number of characters before the list is filtered */
const SEARCH_MIN_LENGTH = 3;

@Component({
  selector: 'my-chainstore',
  imports: [CommonModule, GoogleMapsRendererComponent, GsIconComponent, OpeningHoursComponent, ReactiveFormsModule, TranslatePipe],
  templateUrl: './my-chainstore.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: contents;
    }
  `
})
export class MyChainstoreComponent {
  // Services
  protected readonly myChainstore = inject(MyChainstoreService);
  protected readonly styleService = inject(StyleService);
  private readonly buttonStyleHelper = inject(ButtonStyleHelper);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly environmentService = inject(EnvironmentService);
  private readonly openingTimesService = inject(OpeningTimesService);
  private readonly systemService = inject(SystemService);

  // Properties & Signals
  /** Whether a Google Maps key is configured — without one the panel shows the plain store list instead of the renderer's error box */
  protected readonly showMap: Signal<boolean> = computed(() => {
    const key = this.environmentService.env.GOOGLE_MAPS_KEY;
    return !!key && key !== 'shouldBeOverwritten' && this.myChainstore.mapLocations().length > 0;
  });
  /** Store whose opening hours are expanded; only one is open at a time */
  protected readonly expandedStore: WritableSignal<number | string | undefined> = signal(undefined);
  /** The formcontrol for the zip/city search */
  protected readonly searchControl = new FormControl('');
  /** Element that had focus when the panel was opened, so it can be restored on close */
  private trigger: HTMLElement | null = null;

  // Getters & Setters
  /** @returns Button settings merged from the defaults and the global config */
  protected get buttonSettings(): Partial<IUiButtonSettings> {
    return {
      ...DEFAULT_CONFIG.uiElements?.buttons,
      ...this.configService.config()?.uiElements?.buttons
    };
  }
  /** @returns Primary action button CSS classes */
  protected get actionButtonClasses(): string {
    return this.buttonStyleHelper.getPrimaryButtonClasses(this.buttonSettings);
  }
  /** @returns Inline styles for button padding and font */
  protected get actionButtonStyles(): Record<string, string> {
    return this.buttonStyleHelper.getButtonStyles(this.buttonSettings);
  }
  /** @returns Border-radius class for the search bar, derived from the configurable button settings */
  protected get searchBarBorderRadiusClass(): string {
    return this.styleService.getBorderRadiusClass(this.buttonSettings.borderRadius);
  }

  // ViewChilds
  /** The panel element, focused while the dialog is open */
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Moves focus into the dialog while it is open and back to the trigger afterwards
    effect(() => {
      const open = this.myChainstore.panelOpen();
      if (!this.systemService.browser) return;
      untracked(() => {
        if (open) {
          this.trigger = document.activeElement as HTMLElement | null;
          this.panel()?.nativeElement.focus();
          return;
        }
        this.trigger?.focus();
        this.trigger = null;
      });
    });
    // Filters the list (debounced) whenever the search term changes; below the minimum length the full list is shown
    this.searchControl.valueChanges
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => {
        const value = term?.trim() ?? '';
        this.myChainstore.search.set(value.length >= SEARCH_MIN_LENGTH ? value : '');
      });
  }

  // Methods
  /**
   * Closes the panel on Escape. Bound to the document because the sidebar trigger hides itself on click,
   * which would otherwise leave focus outside the dialog.
   * @param event The keyboard event
   */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.myChainstore.panelOpen()) this.myChainstore.closePanel();
  }

  /** Clears the search input and shows the full list again. */
  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  /**
   * Tracking key for the store list.
   * @param store The store to build the key for
   * @returns The store id, falling back to its name when the backend delivers no id
   */
  protected trackStore(store: IChainStoreListItem): number | string {
    return store.iD ?? store.name;
  }

  /**
   * Expands or collapses the opening hours of a store.
   * @param store The store whose opening hours were clicked
   */
  protected toggleOpeningHours(store: IChainStoreListItem): void {
    const key = this.trackStore(store);
    this.expandedStore.update((current) => (current === key ? undefined : key));
  }

  /**
   * Whether a store has opening hours to expand — without entries the expanded area would stay empty.
   * @param store The store to check
   * @returns true when the store carries at least one opening-time entry
   */
  protected hasOpeningHours(store: IChainStoreListItem): boolean {
    return this.getOpeningHours(store)?.some((entry) => !entry.closed) ?? false;
  }

  /**
   * The weekly opening times of a store, mirroring `chainstore-item`. Special times do not replace them;
   * they only drive today's text via `OpeningTimesService`. The `chainstorelist` endpoint delivers the
   * entries as `openingHours`, so an empty `openingTimesInfos` falls through to it.
   * @param store The store to read the entries from
   * @returns The entries, or undefined when the store carries none
   */
  protected getOpeningHours(store: IChainStoreListItem): IOpeningTimeInfo[] | undefined {
    return store.openingTimesInfos?.length ? store.openingTimesInfos : store.openingHours;
  }

  /**
   * Builds today's opening-time text for a store.
   * @param store The store to describe
   * @returns Localized text such as "Heute geöffnet von 08:00 - 18:00", or empty when the store has no opening times
   */
  protected getTodaysOpeningTime(store: IChainStoreListItem): string {
    const openingHours = this.getOpeningHours(store);
    if (!this.openingTimesService.hasOpeningTimes(openingHours, store.specialOpeningTimesInfo)) return '';

    return this.openingTimesService.getTodaysOpeningTimeTextForEntries(openingHours, store.specialOpeningTimesInfo);
  }

}
